import {
    getOptionalSurvey,
    saveOptionalSkillClusters,
} from '../actions/surveyActions';
import { breakpoints, colors, fonts } from '../styles';
import { globalColor } from '../utils/globals';
import useViewport from '../utils/useViewport';
import { AnimateItem } from './Animate';
import Button from './Button';
import CircleIcon from './CircleIcon';
import HelpPopin from './HelpPopin';
import ProgressControls from './ProgressControls';
import QuestionText from './QuestionText';
import SurveyPageLayout from './SurveyPageLayout';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

const TextSide = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    box-sizing: border-box;
    ${breakpoints.onlyMobile} {
        text-align: center;
    }
    ${breakpoints.sm} {
        padding-right: 60px;
    }
    ${breakpoints.md} {
        padding-right: 60px;
    }
    ${breakpoints.lg} {
        padding-right: 60px;
    }
    ${breakpoints.largeDesktop} {
        padding-right: 60px;
    }
    ${breakpoints.largerDesktop} {
        padding-right: 60px;
    }
`;

const Title = styled.div`
    ${breakpoints.onlyMobile} {
        justify-content: space-between;
        display: flex;
    }
    ${fonts.content};
    color: ${(_) => globalColor.titleColor};
    text-transform: uppercase;
    margin-bottom: 32px;
`;

const Question = styled.div`
    font-size: 26px;
    ${breakpoints.onlyMobile} {
        font-size: 1.5em;
    }
    color: ${colors.libertyBlue};
    position: relative;
    font-family: 'Open Sans', sans-serif;
    font-weight: bold;
    margin-bottom: 32px;
`;

const IllustrationSide = styled.div`
    box-sizing: border-box;
    padding-left: 30px;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
`;

const PositionedCircleIcon = styled(CircleIcon)`
    align-self: flex-end;
`;

const HelpCircleIcon = styled(CircleIcon)`
    display: flex;
    align-items: center;
    margin-left: -4px;

    ${breakpoints.onlyMobile} {
        margin-left: 3px;
    }
`;

const Illustration = styled.div`
    align-self: center;
`;

const SizedProgressControls = styled(ProgressControls)`
    width: 150px;
    align-self: center;
`;

const HelpIndicator = styled.div`
    margin-top: 1em;
    ${fonts.content};
    color: ${colors.libertyBlue};
    text-decoration: underline;
`;

const ProgressIndicator = styled.div`
    ${fonts.content};
    color: ${colors.libertyBlue};
`;

const TargetSample = styled.div`
    color: ${colors.libertyBlue};
    font-size: 18px;
    ${breakpoints.onlyMobile} {
        font-size: 1em;
    }
    margin-bottom: 32px;

    span {
        font-weight: bold;
    }
`;

const ExplanationPopupNote = styled.div`
    color: ${colors.libertyBlue};
    font-size: 16px;
    ${breakpoints.onlyMobile} {
        font-size: 1em;
    }
    margin-top: -16px;
    margin-bottom: 24px;
    span {
        font-weight: bold;
    }
`;

const HelpContentPlaceholder = styled.div`
    display: flex;
    justify-content: center;
`;

const HelpContent = styled.div`
    display: flex;
    position: relative;
    align-items: center;

    ${breakpoints.onlyMobile} {
        justify-content: center;
    }
`;

const HelpContentWapper = styled.div`
    display: flex;
    margin-top: 24px;
    text-decoration: underline;

    ${breakpoints.onlyMobile} {
        margin-top: 16px;
        justify-content: center;
    }
`;

const FinishSurveyContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-top: 24px;
    ${breakpoints.onlyMobile} {
        align-items: center;
    }
`;

const FinishSurveyText = styled.div`
    color: ${colors.libertyBlue};
    font-size: 18px;
    margin-bottom: 12px;
    text-align: left;
    line-height: 1.5;
    strong {
        font-weight: bold;
    }
    ${breakpoints.onlyMobile} {
        text-align: center;
    }
`;

const FinishSurveyButton = styled(Button)`
    font-size: 18px;
    &:focus-visible {
        outline: none;
    }
    ${breakpoints.onlyMobile} {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        padding: 10px;
    }
`;

const SkillProficiencyLevels = styled.div`
    color: ${colors.libertyBlue};
    font-size: 14px;
    ${breakpoints.onlyMobile} {
        font-size: 12px;
    }
    margin-bottom: 24px;
`;

const SkillProficiencyTitle = styled.div`
    font-weight: bold;
    margin-bottom: 8px;
`;

const ProficiencyList = styled.ul`
    margin: 0;
    padding-left: 20px;
    text-align: left;
    ${breakpoints.onlyMobile} {
        text-align: left;
    }
`;

const ProficiencyItem = styled.li`
    margin-bottom: 4px;
`;

const ProficiencyLabel = styled.span`
    font-weight: bold;
`;

const FixedText = styled.div`
    color: ${colors.libertyBlue};
    font-size: 18px;
    ${breakpoints.onlyMobile} {
        font-size: 1em;
        text-align: center;
    }
    margin-bottom: 32px;
    text-align: left;
`;

interface SurveyPageProps {
    title: string;
    questionPhrase: string;
    questionDomainExplanation: string;
    questionDomain: string;
    doneText?: string;
    helpText?: string;
    progress: number;
    progressTotal: number;
    nextClick?: () => void;
    backClick?: () => void;
    illustrationSideContent?: React.ReactElement;
    showHelpByDefault?: boolean;
    targetSample?: string;
    shouldShowTooltip?: boolean;
    children?: React.ReactNode;
    logoImage: any;
    shouldShowQuestionPhrase?: boolean;
    shouldShowProficiencyHelp?: boolean;
    showFinishSurveyButton?: boolean;
    isReviewMode?: boolean;
}

function useLocationState() {
    const location = useLocation();
    const stateVar = location.state || {};
    return {
        emailAuth: stateVar.emailAuth,
        levels_state: stateVar.levels_state,
    };
}

const SurveyPageTemplate: React.FC<SurveyPageProps> = ({
    title,
    questionPhrase,
    shouldShowQuestionPhrase = true,
    questionDomainExplanation,
    questionDomain,
    progress,
    progressTotal,
    doneText,
    helpText,
    children,
    nextClick,
    backClick,
    illustrationSideContent,
    showHelpByDefault = false,
    shouldShowProficiencyHelp = true,
    targetSample,
    logoImage,
    shouldShowTooltip = true,
    showFinishSurveyButton = false,
    isReviewMode = false,
}): React.ReactElement => {
    const [showHelp, setShowHelp] = useState(showHelpByDefault);
    const { t } = useTranslation(['Survey']);
    const { width } = useViewport();
    const { survey_id, uid } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { emailAuth, levels_state } = useLocationState();

    const handleFinishSurveyClick = async () => {
        const previousPath = `/${survey_id}/${uid}/mandatory-welcome`;

        // Wait for the second action to complete
        await dispatch(getOptionalSurvey(survey_id, uid, false));

        // Only proceed to navigation after both actions have completed
        navigate(previousPath, {
            state: {
                levels_state: levels_state,
                emailAuth: emailAuth,
            },
        });
    };
    const dataSet = sessionStorage.getItem(`levels_config_${survey_id}`);

    return showHelp ? (
        <HelpPopin onClose={() => setShowHelp(false)} />
    ) : (
        <SurveyPageLayout logoImage={logoImage}>
            <TextSide>
                <Title>
                    {width <= breakpoints.value.mobile && backClick && (
                        <CircleIcon
                            backgroundColor={globalColor.backgroundColor2}
                            onClick={() => backClick()}
                            type={'previousMobile'}
                            active={true}
                        />
                    )}
                    {title}
                    {width <= breakpoints.value.mobile && (
                        <ProgressIndicator>
                            {progress.toString()}/{progressTotal.toString()}
                        </ProgressIndicator>
                    )}
                </Title>
                <AnimateItem changeOn={questionDomain}>
                    <Question>
                        {doneText || (
                            <QuestionText
                                shoudShowTooltip={shouldShowTooltip}
                                shouldShowQuestionPhrase={
                                    shouldShowQuestionPhrase
                                }
                                questionPhrase={questionPhrase}
                                questionDomain={questionDomain}
                                questionDomainExplanation={
                                    questionDomainExplanation
                                }
                            />
                        )}
                    </Question>
                    {shouldShowTooltip && (
                        <ExplanationPopupNote
                            dangerouslySetInnerHTML={{
                                __html:
                                    width <= breakpoints.value.mobile
                                        ? t('tooltipNoteMobile')
                                        : t('tooltipNoteDesktop'),
                            }}
                        />
                    )}
                    {targetSample && (
                        <TargetSample
                            dangerouslySetInnerHTML={{
                                __html: targetSample,
                            }}
                        />
                    )}
                    {!isReviewMode &&
                        dataSet &&
                        (() => {
                            const parsedData = JSON.parse(dataSet);
                            if (
                                parsedData.levels_data &&
                                parsedData.levels_data.length
                            ) {
                                return (
                                    <SkillProficiencyLevels>
                                        <SkillProficiencyTitle>
                                            {t('skillProficiency')}
                                        </SkillProficiencyTitle>
                                        <ProficiencyList>
                                            {parsedData.levels_data.map(
                                                (level: any) => (
                                                    <ProficiencyItem
                                                        key={level.id}
                                                    >
                                                        <ProficiencyLabel>
                                                            {level.answerLevel}:
                                                        </ProficiencyLabel>{' '}
                                                        <span
                                                            dangerouslySetInnerHTML={{
                                                                __html: `${level.supportingText}`,
                                                            }}
                                                        />
                                                    </ProficiencyItem>
                                                ),
                                            )}
                                        </ProficiencyList>
                                    </SkillProficiencyLevels>
                                );
                            }
                            return null;
                        })()}
                    {!isReviewMode && (
                        <FixedText>{t('proficiencyExplanation')}</FixedText>
                    )}

                    {children}
                    {/* {width <= breakpoints.value.mobile && (
                        <HelpIndicator onClick={() => setShowHelp(true)}>
                            {helpText}
                        </HelpIndicator>
                    )} */}
                    {shouldShowProficiencyHelp && !doneText && (
                        <>
                            <HelpContentWapper>
                                <HelpContent>
                                    {' '}
                                    <div
                                        style={{
                                            padding: '5px',
                                            fontSize: '18px',
                                            color: 'black',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                        onClick={() => setShowHelp(true)}
                                    >
                                        {' '}
                                        {t('skillProficiencyDefinition')}
                                    </div>
                                    <HelpCircleIcon
                                        type={'help'}
                                        active={true}
                                        backgroundColor={
                                            globalColor.backgroundColor1
                                        }
                                        onClick={() => setShowHelp(true)}
                                    />
                                </HelpContent>
                            </HelpContentWapper>
                            {showFinishSurveyButton && !isReviewMode && (
                                <FinishSurveyContainer>
                                    <FinishSurveyText
                                        dangerouslySetInnerHTML={{
                                            __html: t('finishSurveyText'),
                                        }}
                                    />
                                    <FinishSurveyButton
                                        buttonType="secondary"
                                        className="finish-survey-button"
                                        borderColor={globalColor.primaryButton}
                                        textColor={globalColor.primaryButton}
                                        onClick={handleFinishSurveyClick}
                                    >
                                        {t('FinishSurvey')}
                                    </FinishSurveyButton>
                                </FinishSurveyContainer>
                            )}
                            {isReviewMode && (
                                <FinishSurveyContainer>
                                    <FinishSurveyText
                                        dangerouslySetInnerHTML={{
                                            __html: t('backToListText'),
                                        }}
                                    />
                                    <FinishSurveyButton
                                        buttonType="secondary"
                                        className="finish-survey-button"
                                        borderColor={globalColor.primaryButton}
                                        textColor={globalColor.primaryButton}
                                        onClick={handleFinishSurveyClick}
                                    >
                                        {t('BackToList')}
                                    </FinishSurveyButton>
                                </FinishSurveyContainer>
                            )}
                        </>
                    )}
                </AnimateItem>
            </TextSide>
            {width > breakpoints.value.mobile && (
                <IllustrationSide>
                    <HelpContentPlaceholder>&nbsp;</HelpContentPlaceholder>
                    <Illustration>{illustrationSideContent}</Illustration>
                    <SizedProgressControls
                        count={progress}
                        total={progressTotal}
                        onClickForward={nextClick}
                        onClickBackward={backClick}
                    />
                </IllustrationSide>
            )}
        </SurveyPageLayout>
    );
};

export default SurveyPageTemplate;
